import { useState, useEffect } from "react";
import {
  getBossQuest,
  getDailyQuestRowsInRange,
  getProfile,
  listXPHistory,
} from "../api/supabaseData";
import { getWeekId } from "../utils/bossQuestUtils";
import { localToDateString } from "../utils/dateUtils";

// Returns daily XP, penalties, completions for last 30 days
export function useAnalytics(uid, refreshKey = "", timeZone = null) {
  const [analytics, setAnalytics] = useState({
    xpByDate: [],
    penaltiesByDate: [],
    completionsByDate: [],
    streak: 0,
    bestStreak: 0,
    totalXP: 0,
    totalQuests: 0,
    totalPenalties: 0,
    completionRate7d: 0,
    penaltyRate7d: 0,
    completedQuestsLast7d: 0,
    penaltiesLast7d: 0,
    currentWeekBossQuest: null,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!uid) {
      setLoading(false);
      return;
    }
    let cancelled = false;

    (async () => {
      setLoading(true);
      try {
        const today = new Date();
        const dateStrings = Array.from({ length: 30 }, (_, index) => {
          const date = new Date(today);
          date.setDate(today.getDate() - index);
          return localToDateString(date);
        });

        const [xpHistory, profile, dailyRows, bossQuest] = await Promise.all([
          listXPHistory(uid),
          getProfile(uid),
          getDailyQuestRowsInRange(uid, dateStrings[dateStrings.length - 1], dateStrings[0]),
          getBossQuest(uid, getWeekId(today, timeZone)),
        ]);

        const xpMap = {};
        let historyXP = 0;
        xpHistory.forEach((entry) => {
          const day = entry.date;
          if (!day) return;
          xpMap[day] = (xpMap[day] || 0) + Number(entry.xp || 0);
          historyXP += Number(entry.xp || 0);
        });
        const orderedDates = [...dateStrings].reverse();
        const xpByDate = orderedDates.map((date) => ({
          date,
          xp: Number(xpMap[date] || 0),
        }));

        const dailyMap = new Map(
          (dailyRows || []).map((row) => [row.quest_date, Array.isArray(row.quests) ? row.quests : []])
        );

        let totalQuests = 0;
        let totalPenalties = 0;
        const dayStats = dateStrings
          .map((dateStr) => {
            const quests = dailyMap.get(dateStr) || [];
            const completed = quests.filter((q) => q.status === "completed").length;
            const penalties = quests.filter((q) => q.isPenalty).length;
            const allComplete = quests.length > 0 && completed === quests.length;
            totalQuests += quests.length;
            totalPenalties += penalties;
            return { date: dateStr, completed, penalties, allComplete };
          })
          .reverse();

        const completionsByDate = dayStats.map(({ date, completed }) => ({
          date,
          count: completed,
        }));
        const penaltiesByDate = dayStats.map(({ date, penalties }) => ({
          date,
          count: penalties,
        }));

        let bestStreak = 0;
        let runningStreak = 0;
        dayStats.forEach(({ allComplete }) => {
          if (allComplete) {
            runningStreak += 1;
            bestStreak = Math.max(bestStreak, runningStreak);
          } else {
            runningStreak = 0;
          }
        });

        let streak = 0;
        for (let index = dayStats.length - 1; index >= 0; index -= 1) {
          if (!dayStats[index].allComplete) break;
          streak += 1;
        }

        const last7Stats = dayStats.slice(-7);
        const completedQuestsLast7d = last7Stats.reduce(
          (sum, day) => sum + Number(day.completed || 0),
          0
        );
        const penaltiesLast7d = last7Stats.reduce(
          (sum, day) => sum + Number(day.penalties || 0),
          0
        );
        const questCountLast7d = last7Stats.reduce((sum, day) => {
          const quests = dailyMap.get(day.date) || [];
          return sum + quests.length;
        }, 0);
        const completionRate7d = questCountLast7d
          ? Math.round((completedQuestsLast7d / questCountLast7d) * 100)
          : 0;
        const penaltyRate7d = questCountLast7d
          ? Math.round((penaltiesLast7d / questCountLast7d) * 100)
          : 0;

        if (!cancelled) {
          setAnalytics({
            xpByDate,
            penaltiesByDate,
            completionsByDate,
            streak,
            bestStreak,
            totalXP: Number(profile?.totalXP ?? historyXP ?? 0),
            totalQuests,
            totalPenalties,
            completionRate7d,
            penaltyRate7d,
            completedQuestsLast7d,
            penaltiesLast7d,
            currentWeekBossQuest: bossQuest || null,
          });
        }
      } catch (error) {
        if (!cancelled) {
          console.error("Failed to load analytics:", error);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [uid, refreshKey, timeZone]);

  return { analytics, loading };
}
